import { useCallback, useEffect, useRef, useState } from 'react';
import { checkGuestContact, GuestContactWarning } from '../services/GuestContactCheckService';

export const DUPLICATE_CHECK_DEBOUNCE_MS = 400;

interface Options {
  field: 'phone' | 'email';
  value: string;
  /** The value the record already has (edit); an unchanged value is never checked. */
  initialValue?: string;
  /** Guest ref (GS-###) being edited. */
  excludeGuest?: string | null;
  /** The value looks complete enough to ask about. Pass a function with a stable identity (module level). */
  isValid?: (value: string) => boolean;
  debounceMs?: number;
}

const clean = (value: string | undefined) => (value ?? '').trim();

/**
 * Inline duplicate warning for one guest contact field. Asks the API once the field has been left (blur) and the
 * value is valid, debounced; asks again if the value then changes. Never asks for an empty value or for the value
 * the record already had. A failed call shows nothing. The warning can be dismissed per value and NEVER gates saving.
 */
export function useDuplicateContactWarning({ field, value, initialValue, excludeGuest, isValid, debounceMs = DUPLICATE_CHECK_DEBOUNCE_MS }: Options) {
  const [touched, setTouched] = useState(false);
  const [warning, setWarning] = useState<GuestContactWarning | null>(null);
  const [dismissedFor, setDismissedFor] = useState<string | null>(null);
  const requestId = useRef(0);
  const current = clean(value);

  useEffect(() => {
    const id = ++requestId.current;
    setWarning(null);
    if (!touched || !current || current === clean(initialValue) || (isValid && !isValid(current))) return undefined;
    const timer = setTimeout(() => {
      checkGuestContact({ [field]: current, exclude_guest: excludeGuest ?? null }).then((warnings) => {
        if (id !== requestId.current) return; // the value changed since
        setWarning(warnings.find((w) => w.field === field && w.guest) ?? null);
      });
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [touched, current, initialValue, excludeGuest, field, debounceMs, isValid]);

  const onBlur = useCallback(() => setTouched(true), []);
  const dismiss = useCallback(() => setDismissedFor(current), [current]);

  return { warning: warning && dismissedFor !== current ? warning : null, onBlur, dismiss };
}
